import OutlineTitle from "@/components/OutlineTitle";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { simhubAPI } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import { Activity, Clock, Flag, Gamepad2, Info, Timer, Gauge, Car, MapPin, Globe2, Lock, Inbox, Check, X, Send } from "lucide-react";
import SimHubDownloadAppCard from "@/components/SimHubDownloadAppCard";
import SimHubRelayDashboard from "@/components/SimHubRelayDashboard";
import SessionDetailModal from "@/components/SessionDetailModal";

function Metric({ icon: Icon, label, value }) {
  return (
    <Card className="border-border bg-card p-4">
      <Icon className="mb-2 h-5 w-5 text-primary" />
      <p className="font-display text-2xl font-bold">{value}</p>
      <p className="text-xs text-muted-foreground">{label}</p>
    </Card>
  );
}

const formatLapTime = (ms) => {
  if (!ms || ms <= 0) return "--:--.---";
  const minutes = Math.floor(ms / 60000);
  const seconds = Math.floor((ms % 60000) / 1000);
  const millis = ms % 1000;
  return `${minutes}:${String(seconds).padStart(2, "0")}.${String(millis).padStart(3, "0")}`;
};

export default function SimHub() {
  const [summary, setSummary] = useState(null);
  const [profile, setProfile] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openSessionId, setOpenSessionId] = useState(null);
  const [openPublicId, setOpenPublicId] = useState(null);

  // Browse + Share Requests state
  const [discoverable, setDiscoverable] = useState(null);
  const [incomingShares, setIncomingShares] = useState(null);
  const [outgoingShares, setOutgoingShares] = useState(null);

  const loadDiscoverable = () =>
    simhubAPI.listDiscoverable({ limit: 100 }).then((r) => setDiscoverable(r.data || [])).catch(() => setDiscoverable([]));
  const loadShares = () =>
    Promise.all([simhubAPI.listIncomingShares(), simhubAPI.listOutgoingShares()])
      .then(([inc, out]) => { setIncomingShares(inc.data || []); setOutgoingShares(out.data || []); })
      .catch(() => { setIncomingShares([]); setOutgoingShares([]); });

  useEffect(() => {
    Promise.all([
      simhubAPI.getSummary().catch(() => ({ data: {} })),
      simhubAPI.getUserProfile().catch(() => ({ data: {} })),
      simhubAPI.getSessions().catch(() => ({ data: [] })),
    ]).then(([s, p, se]) => {
      setSummary(s.data || {});
      setProfile(p.data || {});
      setSessions(Array.isArray(se.data) ? se.data : se.data?.sessions || []);
      setLoading(false);
    });
    // Prefetch shares badge count on initial load
    simhubAPI.listIncomingShares().then((r) => setIncomingShares(r.data || [])).catch(() => setIncomingShares([]));
  }, []);

  const pendingIncoming = (incomingShares || []).filter((s) => s.status === "pending");

  const requestAccess = async (sessionId) => {
    try {
      const r = await simhubAPI.createShareRequest(sessionId, "I'd like to compare laps.");
      if (r.data?.status === "approved") toast.success("Session is public — opening now…");
      else toast.success("Request sent to the owner");
      loadDiscoverable();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not send request"); }
  };
  const approveShare = async (id) => {
    try { await simhubAPI.approveShare(id); toast.success("Approved — you earned points"); loadShares(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not approve"); }
  };
  const denyShare = async (id) => {
    try { await simhubAPI.denyShare(id); toast.success("Denied"); loadShares(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not deny"); }
  };

  if (loading) return <div className="mx-auto max-w-5xl space-y-4"><Skeleton className="h-24 rounded-lg" /><Skeleton className="h-64 rounded-lg" /></div>;

  const empty = (summary?.total_sessions || 0) === 0 && (profile?.total_sessions || 0) === 0;
  const topSpeed = profile?.overall_top_speed_kmh || 0;
  const topSpeedMeta = profile?.overall_top_speed_meta;

  return (
    <div className="mx-auto max-w-5xl space-y-6">
      <div>
        <OutlineTitle>SimHub Stats</OutlineTitle>
        <p className="text-sm text-muted-foreground">Your recorded telemetry sessions & personal bests</p>
      </div>

      <div className="flex items-start gap-2 rounded-lg border border-border bg-secondary/40 p-3 text-sm text-muted-foreground">
        <Info className="mt-0.5 h-4 w-4 flex-shrink-0 text-primary" />
        Pair below to stream live telemetry from SimHub on your PC. Recorded sessions from either the mobile app or web live here.
      </div>

      <SimHubDownloadAppCard />

      <SimHubRelayDashboard />

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
        <Metric icon={Activity} label="Sessions" value={profile?.total_sessions ?? summary?.total_sessions ?? 0} />
        <Metric icon={Flag} label="Total laps" value={profile?.total_laps ?? summary?.total_laps ?? 0} />
        <Metric icon={Clock} label="Hours" value={Number(summary?.total_time_hours || 0).toFixed(1)} />
        <Metric icon={MapPin} label="Tracks" value={profile?.unique_tracks ?? 0} />
        <Metric icon={Car} label="Cars" value={profile?.unique_cars ?? 0} />
      </div>

      {topSpeed > 0 && (
        <Card className="flex items-center gap-3 border-border bg-gradient-to-br from-primary/15 to-card p-4">
          <Gauge className="h-8 w-8 text-primary" />
          <div className="flex-1 min-w-0">
            <p className="text-xs uppercase tracking-wide text-muted-foreground">Top speed (all sessions)</p>
            <p className="font-display text-2xl font-bold">{topSpeed.toFixed(1)} km/h</p>
            {topSpeedMeta && (
              <p className="truncate text-xs text-muted-foreground">
                {topSpeedMeta.car_model} · {topSpeedMeta.track_name}
              </p>
            )}
          </div>
        </Card>
      )}

      {empty ? (
        <Card className="flex flex-col items-center border border-dashed border-border bg-card py-16 text-center">
          <Timer className="mb-3 h-12 w-12 text-muted-foreground" />
          <p className="font-medium">No sessions recorded yet</p>
          <p className="text-sm text-muted-foreground">Race a session with SimHub connected on mobile to see stats here.</p>
        </Card>
      ) : (
        <Tabs defaultValue="bests">
          <TabsList>
            <TabsTrigger value="bests" data-testid="simhub-tab-bests">Personal Bests</TabsTrigger>
            <TabsTrigger value="tracks" data-testid="simhub-tab-tracks">Tracks</TabsTrigger>
            <TabsTrigger value="cars" data-testid="simhub-tab-cars">Cars</TabsTrigger>
            <TabsTrigger value="topspeed" data-testid="simhub-tab-topspeed">Top Speeds</TabsTrigger>
            <TabsTrigger value="sessions" data-testid="simhub-tab-sessions">Sessions</TabsTrigger>
            <TabsTrigger value="browse" data-testid="simhub-tab-browse" onClick={() => discoverable === null && loadDiscoverable()}>Browse</TabsTrigger>
            <TabsTrigger value="requests" data-testid="simhub-tab-requests" onClick={() => (incomingShares === null || outgoingShares === null) && loadShares()}>
              Share requests{pendingIncoming.length > 0 ? <Badge className="ml-2 border-0 bg-primary text-primary-foreground">{pendingIncoming.length}</Badge> : null}
            </TabsTrigger>
          </TabsList>

          <TabsContent value="bests" className="mt-4">
            <Card className="divide-y divide-border border-border bg-card">
              {(profile?.best_lap_per_track || []).length === 0 ? (
                <p className="p-6 text-center text-sm text-muted-foreground">No valid laps recorded yet.</p>
              ) : (
                <div>
                  <div className="hidden grid-cols-[1fr_160px_60px_60px_120px] gap-3 px-3 py-2 text-xs font-bold uppercase tracking-wide text-muted-foreground md:grid">
                    <span>Track · Car</span>
                    <span>Best lap</span>
                    <span className="text-center">ABS</span>
                    <span className="text-center">TC</span>
                    <span className="text-right">Date</span>
                  </div>
                  {profile.best_lap_per_track.map((b, i) => (
                    <div key={i} className="grid grid-cols-[1fr_auto] items-center gap-3 p-3 text-sm md:grid-cols-[1fr_160px_60px_60px_120px]" data-testid={`simhub-pb-${i}`}>
                      <div className="min-w-0">
                        <p className="truncate font-medium">{b.track_name}</p>
                        <p className="truncate text-xs text-muted-foreground">{b.car_model}{b.car_class ? ` · ${b.car_class}` : ""}</p>
                      </div>
                      <Badge className="w-fit border-0 bg-primary/20 font-mono text-primary">{formatLapTime(b.lap_time_ms)}</Badge>
                      <span className="hidden text-center text-xs text-muted-foreground md:inline">{b.abs_level != null ? b.abs_level : "—"}</span>
                      <span className="hidden text-center text-xs text-muted-foreground md:inline">{b.tc_level != null ? b.tc_level : "—"}</span>
                      <span className="hidden text-right text-xs text-muted-foreground md:inline">{b.date ? new Date(b.date).toLocaleDateString() : ""}</span>
                    </div>
                  ))}
                </div>
              )}
            </Card>
          </TabsContent>

          <TabsContent value="tracks" className="mt-4">
            <Card className="divide-y divide-border border-border bg-card">
              {(profile?.laps_per_track || []).length === 0 ? (
                <p className="p-6 text-center text-sm text-muted-foreground">No tracks yet.</p>
              ) : profile.laps_per_track.map((t, i) => (
                <div key={i} className="grid grid-cols-[1fr_auto] items-center gap-3 p-3 text-sm" data-testid={`simhub-track-${i}`}>
                  <div className="min-w-0">
                    <p className="truncate font-medium">{t.track_name}</p>
                    <p className="truncate text-xs text-muted-foreground font-mono">{t.track_key}</p>
                  </div>
                  <div className="text-right text-xs">
                    <p className="font-bold">{t.laps} laps</p>
                    <p className="text-muted-foreground">{t.sessions} sessions</p>
                  </div>
                </div>
              ))}
            </Card>
          </TabsContent>

          <TabsContent value="cars" className="mt-4">
            <Card className="divide-y divide-border border-border bg-card">
              {(profile?.laps_per_car || []).length === 0 ? (
                <p className="p-6 text-center text-sm text-muted-foreground">No cars yet.</p>
              ) : profile.laps_per_car.map((c, i) => (
                <div key={i} className="grid grid-cols-[1fr_auto] items-center gap-3 p-3 text-sm" data-testid={`simhub-car-${i}`}>
                  <div className="min-w-0">
                    <p className="truncate font-medium">{c.car_model}</p>
                    <p className="truncate text-xs text-muted-foreground">{c.car_class || "unclassified"}</p>
                  </div>
                  <div className="text-right text-xs">
                    <p className="font-bold">{c.laps} laps</p>
                    <p className="text-muted-foreground">{c.sessions} sessions</p>
                  </div>
                </div>
              ))}
            </Card>
          </TabsContent>

          <TabsContent value="topspeed" className="mt-4">
            <Card className="divide-y divide-border border-border bg-card">
              {(profile?.top_speed_per_track || []).length === 0 ? (
                <p className="p-6 text-center text-sm text-muted-foreground">No top speed data yet.</p>
              ) : profile.top_speed_per_track.map((s, i) => (
                <div key={i} className="grid grid-cols-[1fr_auto] items-center gap-3 p-3 text-sm" data-testid={`simhub-topspeed-${i}`}>
                  <div className="min-w-0">
                    <p className="truncate font-medium">{s.track_name}</p>
                    <p className="truncate text-xs text-muted-foreground">{s.car_model}</p>
                  </div>
                  <Badge className="border-0 bg-primary/20 font-mono text-primary">{s.kmh.toFixed(1)} km/h</Badge>
                </div>
              ))}
            </Card>
          </TabsContent>

          <TabsContent value="sessions" className="mt-4">
            <Card className="divide-y divide-border border-border bg-card">
              {sessions.length === 0 ? (
                <p className="p-6 text-center text-sm text-muted-foreground">No sessions recorded yet.</p>
              ) : sessions.map((s, i) => (
                <button
                  type="button"
                  key={s.id || i}
                  onClick={() => setOpenSessionId(s.id)}
                  className="flex w-full items-center justify-between p-3 text-left text-sm transition-colors hover:bg-secondary/40"
                  data-testid={`simhub-session-${i}`}
                >
                  <div className="min-w-0">
                    <p className="truncate font-medium">{s.game_name || "Session"}</p>
                    <p className="truncate text-xs text-muted-foreground">{s.track?.track_display_name || s.track?.track_name || s.track_name} · {s.car?.car_model || s.car_name || s.car?.car_class || ""}</p>
                  </div>
                  <div className="text-right text-xs text-muted-foreground">
                    <p>{s.total_laps ?? s.laps?.length ?? 0} laps</p>
                    <p>{s.started_at ? new Date(s.started_at).toLocaleDateString() : ""}</p>
                  </div>
                </button>
              ))}
            </Card>
          </TabsContent>
          <TabsContent value="browse" className="mt-4">
            <Card className="divide-y divide-border border-border bg-card" data-testid="simhub-browse">
              {discoverable === null ? (
                <div className="p-4 space-y-2">{Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-14 rounded-lg" />)}</div>
              ) : discoverable.length === 0 ? (
                <p className="p-6 text-center text-sm text-muted-foreground">No sessions from other drivers yet.</p>
              ) : discoverable.map((s) => {
                const accessBadge = {
                  public: { label: "Public", className: "bg-emerald-500/20 text-emerald-400", icon: Globe2 },
                  approved: { label: "Approved", className: "bg-emerald-500/20 text-emerald-400", icon: Check },
                  pending: { label: "Pending", className: "bg-amber-500/20 text-amber-400", icon: Send },
                  locked: { label: "Private", className: "bg-muted text-muted-foreground", icon: Lock },
                }[s.access];
                const canOpen = s.access === "public" || s.access === "approved";
                return (
                  <div key={s.id} className="grid grid-cols-[1fr_auto] items-center gap-3 p-3 text-sm" data-testid={`simhub-browse-${s.id}`}>
                    <div className="min-w-0">
                      <p className="truncate font-medium">{s.owner?.username || "Driver"} · {s.track?.track_display_name || s.track?.track_name}</p>
                      <p className="truncate text-xs text-muted-foreground">{s.car?.car_model || "—"}{s.car?.car_class ? ` · ${s.car.car_class}` : ""} · {s.total_laps || s.laps?.length || 0} laps</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge className={`border-0 ${accessBadge.className}`}><accessBadge.icon className="mr-1 h-3 w-3 inline" />{accessBadge.label}</Badge>
                      {canOpen ? (
                        <Button size="sm" variant="outline" onClick={() => setOpenPublicId(s.id)} data-testid={`simhub-browse-compare-${s.id}`}>Compare</Button>
                      ) : s.access === "pending" ? (
                        <Button size="sm" variant="ghost" disabled>Requested</Button>
                      ) : (
                        <Button size="sm" className="bg-primary text-primary-foreground hover:bg-primary/90" onClick={() => requestAccess(s.id)} data-testid={`simhub-browse-request-${s.id}`}>
                          <Send className="mr-1 h-3 w-3" /> Request access
                        </Button>
                      )}
                    </div>
                  </div>
                );
              })}
            </Card>
          </TabsContent>

          <TabsContent value="requests" className="mt-4">
            <div className="space-y-4" data-testid="simhub-requests">
              <Card className="border-border bg-card">
                <div className="flex items-center gap-2 border-b border-border p-3">
                  <Inbox className="h-4 w-4 text-primary" />
                  <p className="font-display text-xs font-bold uppercase tracking-wide">Incoming (your sessions)</p>
                </div>
                {incomingShares === null ? (
                  <div className="p-3 space-y-2">{Array.from({ length: 2 }).map((_, i) => <Skeleton key={i} className="h-14 rounded-lg" />)}</div>
                ) : incomingShares.length === 0 ? (
                  <p className="p-6 text-center text-sm text-muted-foreground">No incoming requests.</p>
                ) : (
                  <div className="divide-y divide-border">
                    {incomingShares.map((r) => (
                      <div key={r._id} className="flex flex-wrap items-center gap-3 p-3 text-sm" data-testid={`simhub-share-incoming-${r._id}`}>
                        <div className="min-w-0 flex-1">
                          <p className="truncate"><span className="font-semibold">{r.requester_username}</span> wants to compare — <span className="capitalize text-muted-foreground">{r.status}</span></p>
                          {r.message && <p className="truncate text-xs text-muted-foreground">"{r.message}"</p>}
                        </div>
                        {r.status === "pending" && (
                          <div className="flex gap-2">
                            <Button size="sm" variant="outline" onClick={() => approveShare(r._id)} data-testid={`simhub-share-approve-${r._id}`}><Check className="mr-1 h-3 w-3" /> Approve</Button>
                            <Button size="sm" variant="ghost" onClick={() => denyShare(r._id)} data-testid={`simhub-share-deny-${r._id}`}><X className="mr-1 h-3 w-3" /> Deny</Button>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </Card>

              <Card className="border-border bg-card">
                <div className="flex items-center gap-2 border-b border-border p-3">
                  <Send className="h-4 w-4 text-primary" />
                  <p className="font-display text-xs font-bold uppercase tracking-wide">Outgoing (your requests)</p>
                </div>
                {outgoingShares === null ? (
                  <div className="p-3 space-y-2">{Array.from({ length: 2 }).map((_, i) => <Skeleton key={i} className="h-14 rounded-lg" />)}</div>
                ) : outgoingShares.length === 0 ? (
                  <p className="p-6 text-center text-sm text-muted-foreground">You haven't requested any sessions yet.</p>
                ) : (
                  <div className="divide-y divide-border">
                    {outgoingShares.map((r) => (
                      <div key={r._id} className="flex items-center justify-between gap-3 p-3 text-sm" data-testid={`simhub-share-outgoing-${r._id}`}>
                        <span className="truncate">Request on session <span className="font-mono text-xs">{r.session_id.slice(-6)}</span></span>
                        <Badge className={`border-0 ${r.status === "approved" ? "bg-emerald-500/20 text-emerald-400" : r.status === "denied" ? "bg-destructive/20 text-destructive" : "bg-amber-500/20 text-amber-400"}`}>{r.status}</Badge>
                      </div>
                    ))}
                  </div>
                )}
              </Card>
            </div>
          </TabsContent>
        </Tabs>
      )}

      <SessionDetailModal
        sessionId={openSessionId}
        open={!!openSessionId}
        onOpenChange={(v) => { if (!v) setOpenSessionId(null); }}
      />
      <SessionDetailModal
        sessionId={openPublicId}
        open={!!openPublicId}
        onOpenChange={(v) => { if (!v) setOpenPublicId(null); }}
        publicView
      />
    </div>
  );
}
